import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { PendingReviewsBanner } from '../../shared/components/pending-reviews-banner/pending-reviews-banner';
import { Batch, DocumentItem } from '../../core/services/batch';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';

interface AccessDetail {
  heading: string;
  evidence: string[];
}

interface UserAccessGroup {
  name: string;
  accesses: AccessDetail[];
}

interface AccessRow {
  userName: string;
  showUser: boolean;
  userRowSpan: number;
  heading: string;
  evidence: string[];
}

@Component({
  selector: 'app-document-access',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, ToastComponent, PendingReviewsBanner],
  templateUrl: './document-access.html',
  styleUrl: './document-access.css'
})
export class DocumentAccess implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private batchService = inject(Batch);
  private toast = inject(Toast);

  docId = signal<string | null>(null);
  docName = signal<string>('');
  batchId = signal<string | null>(null);
  cameFrom = signal<string | null>(null);
  isLoading = signal(true);
  userAccessGroups = signal<UserAccessGroup[]>([]);

  accessRows = computed<AccessRow[]>(() => {
    const rows: AccessRow[] = [];
    for (const group of this.userAccessGroups()) {
      const accesses = group.accesses.length ? group.accesses : [{ heading: '-', evidence: [] }];
      accesses.forEach((access, idx) => {
        rows.push({
          userName: group.name,
          showUser: idx === 0,
          userRowSpan: accesses.length,
          heading: access.heading,
          evidence: access.evidence
        });
      });
    }
    return rows;
  });

  ngOnInit() {
    this.docId.set(this.route.snapshot.paramMap.get('id'));
    const docName = history.state?.docName;
    if (docName) this.docName.set(docName);
    const batchId = history.state?.batchId;
    if (batchId) this.batchId.set(batchId);
    const cameFrom = history.state?.from;
    if (cameFrom) this.cameFrom.set(cameFrom);
    if (this.docId()) {
      this.loadDocAccessDetails(this.docId()!);
    }
  }

  private loadDocAccessDetails(id: string) {
    this.isLoading.set(true);
    this.batchService.getDocumentChunksRoles(id).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        if (history.state?.docName) {
          this.docName.set(history.state.docName);
        }
        this.userAccessGroups.set(this.transformChunksToUserAccessGroups(res.chunks || []));
      },
      error: (err) => {
        this.isLoading.set(false);
        const message = err?.error?.message || 'Failed to load document access details.';
        this.toast.show(message, 'error');
      }
    });
  }

  back() {
    if (this.cameFrom() === 'classification') {
      this.router.navigate(['/documents/classified']);
    } else if (this.batchId()) {
      this.router.navigate(['/batch-details', this.batchId()]);
    } else {
      this.router.navigate(['/manage-batches']);
    }
  }

  getExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  }

  getDocIconClass(fileName: string): string {
    const ext = this.getExtension(fileName);
    if (ext === 'DOCX' || ext === 'DOC') return 'doc-word';
    if (ext === 'XLSX' || ext === 'XLS' || ext === 'CSV') return 'doc-excel';
    if (ext === 'PPTX' || ext === 'PPT') return 'doc-ppt';
    return 'doc-generic';
  }

  private transformChunksToUserAccessGroups(chunks: any[]): UserAccessGroup[] {
    const groupMap = new Map<string, Map<string, Set<string>>>();

    for (const chunk of chunks) {
      const heading = (chunk?.heading || 'Access').trim();
      for (const role of chunk?.roles || []) {
        const userName = (role?.role || 'Unknown User').trim();
        if (!groupMap.has(userName)) {
          groupMap.set(userName, new Map());
        }

        const accessMap = groupMap.get(userName)!;
        if (!accessMap.has(heading)) {
          accessMap.set(heading, new Set());
        }

        for (const item of role?.evidence || []) {
          const evidence = String(item || '').trim();
          if (evidence) {
            accessMap.get(heading)!.add(evidence);
          }
        }
      }
    }

    return Array.from(groupMap.entries())
      .map(([name, accessMap]) => ({
        name,
        accesses: Array.from(accessMap.entries()).map(([heading, evidenceSet]) => ({
          heading,
          evidence: Array.from(evidenceSet)
        }))
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
}
